import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ElementRef, AfterViewInit, AfterContentInit } from '@angular/core';
import { HisBaseGlobalsService, HisNotificationService, HisUiService } from 'hisng';
import {
    ENGLISH_DATE_TIME_FORMAT_PLACEHOLDER,
    ENGLISH_DATE_TIME_PIPE_FORMAT,
    GERMAN_DATE_TIME_FORMAT_PLACEHOLDER,
    GERMAN_DATE_TIME_PIPE_FORMAT
} from 'app/rm/erp/common/model/constants';
import { hasFiles } from 'app/rm/erp/common/utils/event-typeguards';
import { FileUpload } from 'primeng/fileupload';
import { ErpDocumentService } from 'app/rm/erp/common/document/service/erp-document-service';
import { FileSizeUtils } from 'app/rm/erp/common/utils/filesize-utils';
import { Document } from 'app/rm/erp/common/document/model/document';
import { DocumentCategoryValue } from 'app/rm/erp/common/document/model/document-values';

@Component( {
    selector: 'erp-businesstripapplication-documents',
    templateUrl: './businesstripapplication-documents.component.html'
} )
export class BusinesstripApplicationDocumentsComponent implements OnInit {
    @Input() documents: Document[] = [];
    @Output() readonly documentsChange = new EventEmitter<Document[]>();
    @ViewChild( 'downloadLink' ) private downloadLink: ElementRef = new ElementRef( null );
    readonly dateTimeFormat: string;
    readonly datePlaceholderFormat: string;

    toggleUploadButtonDisabled = true;
    maxFileSize = 1000000000;
    maxFiles = 3;
    allowedFileExtensions = '.pdf,.doc,.png,.jpg,.jpeg';

    documentCategoryValues: DocumentCategoryValue[] = [];
    filteredDocumentCategoryValues: DocumentCategoryValue[] = [];

    documentCategory: DocumentCategoryValue | undefined;

    constructor(
        private readonly hisBaseGlobalsService: HisBaseGlobalsService,
        private readonly hisUiService: HisUiService,
        private readonly notificationService: HisNotificationService,
        private readonly documentService: ErpDocumentService,
        private readonly fileSizeUtils: FileSizeUtils
    ) {
        if ( this.hisBaseGlobalsService.getLanguage() === 'de' ) {
            this.dateTimeFormat = GERMAN_DATE_TIME_PIPE_FORMAT;
            this.datePlaceholderFormat = GERMAN_DATE_TIME_FORMAT_PLACEHOLDER;
        } else {
            this.dateTimeFormat = ENGLISH_DATE_TIME_PIPE_FORMAT;
            this.datePlaceholderFormat = ENGLISH_DATE_TIME_FORMAT_PLACEHOLDER;
        }
    }

    ngOnInit(): void {
        this.documentService.getDocumentCategoryValues().subscribe( documentCategoryValues => {
            this.documentCategoryValues = documentCategoryValues;
        } );
    }

    customUploadHandler( event: unknown, fileUpload: FileUpload ) {
        if ( hasFiles( event ) ) {
            const isValid: boolean = this.validateUploadedFiles( event.files, fileUpload );

            if ( isValid ) {
                this.addFiles( event.files, fileUpload );
            }

            this.updateUploadedFileCount( fileUpload );
        }
    }

    async addFiles( files: File[], fileUpload: FileUpload ) {
        for ( const file of files ) {
            const buffer = await file.arrayBuffer();
            const doc: Document = {
                filename: file.name,
                filesize: file.size + '',
                file: Array.from( new Uint8Array( buffer ) ),
                documentCategory: this.documentCategory,
            };
            this.documents.push( doc );
            this.updateUploadedFileCount( fileUpload );
        }
        this.documentsChange.emit( this.documents );
    }

    /* Aktualisiert die FileUploadkomponente. Wichtig ist dabei der Aufruf der (method) ChangeDetectorRef.markForCheck()
       zum Aktualisieren des Uploadbuttons (siehe markForCheck-Beschreibung) */
    updateUploadedFileCount( fileUpload: FileUpload ) {
        fileUpload.uploadedFileCount = this.documents.length;
        fileUpload.files = [];
        fileUpload.cd.markForCheck();
    }

    validateUploadedFiles( files: File[], fileUpload: FileUpload ): boolean {
        const isFileWithSameNamePresent: boolean = this.isFileWithSameNamePresent( files );

        if ( isFileWithSameNamePresent ) {
            fileUpload.msgs?.push( {
                severity: 'error',
                summary: this.hisUiService.translate( 'rm.erp.common.documents.upload.filenameExists' ),
                detail: ''
            } );

            return false;
        }

        return true;
    }

    downloadDocument( document: Document ): void {
        const blob =
            new Blob( [this.base64ToByteArray( String( document.file ) )], { type: 'application/octet-stream' } );
        const url = window.URL.createObjectURL( blob );

        const link = this.downloadLink.nativeElement;
        link.href = url;
        link.download = document.filename;
        link.click();

        window.URL.revokeObjectURL( url );
    }

    removeFile( event: Event, index: number, fileUpload: FileUpload ) {
        if ( index > -1 ) {
            this.documents.splice( index, 1 );
            fileUpload.remove( event, index );
            this.updateUploadedFileCount( fileUpload );
            this.documentsChange.emit( this.documents );
        }
    }

    getDocumentUploadHelptext( fileLimit: number, maxFileSize: number, fileTypes: string ): string {
        const maxFileSizeMB = this.fileSizeUtils.convertByteToMegaByte( Number( maxFileSize ) );
        return this.hisUiService.translate( 'rm.erp.common.documents.uploadFile.informationtext', fileLimit, maxFileSizeMB, fileTypes );
    }

    isFileWithSameNamePresent( files: File[] ): boolean {
        return this.documents.findIndex( document =>
            files.findIndex( file => file.name === document.filename ) !== -1
        ) !== -1;
    }

    /* Filtere Dokumentenkategorien danach, ob Feld qis_bewerbermanagement_relevant ("relevantForBwm") gesetzt ist. */
    relevantDocumentCategoryValues() {
        this.filteredDocumentCategoryValues = [];
        this.documentCategoryValues.forEach( documentCategoryValue => {
            if ( documentCategoryValue.relevantForRka ) {
                this.filteredDocumentCategoryValues.push( documentCategoryValue );
            }
        } );
    }

    fetchRelevantValues( name: string ) {
        switch ( name ) {
            case 'documentCategory':
                this.relevantDocumentCategoryValues();
                break;
        }
    }

    toggleDocumentUploadButton( event: unknown, fileUpload: FileUpload ) {
        if ( event == null ) {
            this.toggleUploadButtonDisabled = true;
            return;
        }
        this.toggleUploadButtonDisabled = false;
        this.updateUploadedFileCount( fileUpload );
    }

    private base64ToByteArray( base64String: string ): Uint8Array {
        const binaryString = window.atob( base64String );
        const len = binaryString.length;
        const bytes = new Uint8Array( len );
        for ( let i = 0; i < len; i++ ) {
            bytes[i] = binaryString.charCodeAt( i );
        }

        return bytes;
    }
}


